/**
 * PipelineAlert — global banner that warns when the daily training pipeline
 * has not run on schedule.
 *
 * Polls /api/system/pipeline every 10 minutes. If health.status is "warning"
 * or "error", a dismissible bar is rendered between the navbar and main content.
 *
 * The user can dismiss the banner for the current session; it reappears on
 * next page load or if the status changes.
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'

interface PipelineHealth {
  status: 'ok' | 'running' | 'warning' | 'error'
  message: string
  days_since: number | null
}

interface PipelineStatus {
  health: PipelineHealth
  next_run: string
  last_run: { date?: string; finished_at?: string; summary?: { overall_ok?: boolean } }
}

const POLL_MS = 10 * 60 * 1000  // 10 minutes

function fetchPipelineStatus(): Promise<PipelineStatus> {
  // Use the authenticated API client so the JWT Bearer token is included.
  return api.get<PipelineStatus>('/api/system/pipeline')
}

export default function PipelineAlert() {
  const [status, setStatus] = useState<PipelineStatus | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const load = () => {
    fetchPipelineStatus()
      .then(s => {
        setStatus(s)
        // Reset dismissal if a new (different) problem appears
        if (s.health.status !== 'ok' && s.health.status !== 'running') setDismissed(false)
      })
      .catch(() => { /* silently ignore fetch errors */ })
  }

  useEffect(() => {
    load()
    timerRef.current = setInterval(load, POLL_MS)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [])

  // 'running' is healthy: the daily run takes ~an hour, and until 2026-08-24
  // the banner shouted 排程可能漏跑 for that whole hour every session.
  const quiet = status?.health.status === 'ok' || status?.health.status === 'running'
  if (!status || quiet || dismissed) return null

  const isError = status.health.status === 'error'
  const bg    = isError ? 'var(--danger)'  : '#b45309'
  const icon  = isError ? '🚨' : '⚠️'

  // Format next scheduled run as local time
  let nextRunLabel = ''
  try {
    const d = new Date(status.next_run)
    nextRunLabel = d.toLocaleString('zh-TW', {
      month: 'numeric', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { /* ignore */ }

  return (
    <div style={{
      background: bg,
      color: '#fff',
      padding: '8px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      fontSize: 13,
      lineHeight: 1.4,
      flexShrink: 0,
      zIndex: 100,
    }}>
      <span style={{ fontSize: 16 }}>{icon}</span>

      <span style={{ flex: 1 }}>
        <strong>每日訓練排程異常</strong>
        {'：'}
        {status.health.message}
        {nextRunLabel && (
          <span style={{ marginLeft: 12, opacity: 0.85, fontSize: 12 }}>
            下次排程：{nextRunLabel}
          </span>
        )}
        <Link
          to="/system"
          style={{ marginLeft: 12, color: '#fff', textDecoration: 'underline', fontSize: 12 }}
        >
          查看系統頁
        </Link>
      </span>

      <button
        onClick={() => setDismissed(true)}
        title="關閉"
        style={{
          background: 'transparent',
          border: 'none',
          color: '#fff',
          cursor: 'pointer',
          fontSize: 18,
          lineHeight: 1,
          padding: '0 4px',
          opacity: 0.8,
          flexShrink: 0,
        }}
      >
        ×
      </button>
    </div>
  )
}
